import { useEffect, useState, type ReactNode } from 'react';
import { Volume2, Square, Loader2 } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageProvider';
import { useExerciseCoachSpeech } from '@/hooks/useExerciseCoachSpeech';
import { getFitnessGuidedCopy } from '@/lib/fitnessGuidedCopy';
import { getFitnessGuidedPreparationCopy } from '@/lib/fitnessGuidedPreparationCopy';
import type { FitnessGuidedPreparation, FitnessGuidedPreparationAction } from '@/lib/fitnessGuidedPreparation';

interface Props {
  value: FitnessGuidedPreparation;
  scopeId: string;
  paused: boolean;
  onAction: (action: FitnessGuidedPreparationAction) => void;
  instructorSelector?: ReactNode;
  instructorPreview?: ReactNode;
}

export default function GuidedWorkoutPreparation({ value, scopeId, paused, onAction, instructorSelector, instructorPreview }: Props) {
  const { lang, t } = useLanguage();
  const copy = getFitnessGuidedCopy(lang), preparation = getFitnessGuidedPreparationCopy(lang);
  const [hidden, setHidden] = useState(() => document.hidden);
  const introduction = value.stage === 'introduction';
  const title = introduction ? copy.presentation : copy.warmup;
  const body = introduction ? copy.presentationBody : preparation.warmupBody;
  const speech = useExerciseCoachSpeech({
    text: body, identity: JSON.stringify([scopeId, value.stage, lang]), language: lang, paused: paused || hidden,
  });
  useEffect(() => {
    const update = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  const ticking = value.stage === 'warmup' && value.remainingSeconds > 0 && !paused && !hidden;
  useEffect(() => {
    if (!ticking) return;
    // Count visible, unpaused seconds only; never expire while the app is hidden.
    const timer = window.setInterval(() => { if (!document.hidden) onAction('tick'); }, 1000);
    return () => window.clearInterval(timer);
  }, [ticking, onAction]);
  const action = (next: FitnessGuidedPreparationAction) => { speech.stop(); onAction(next); };
  return <section className="mx-4 my-6 rounded-3xl border border-primary/25 bg-card/95 p-5" aria-labelledby="guided-preparation-title">
    <audio ref={speech.attachAudio} playsInline preload="none" className="hidden" aria-hidden="true" />
    <h1 id="guided-preparation-title" className="font-display text-2xl font-bold">{title}</h1>
    <p className="mt-3 text-base leading-relaxed text-muted-foreground">{body}</p>
    {instructorPreview && <div className="mt-4">
      <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{preparation.nextPreview}</p>
      {instructorPreview}
    </div>}
    {instructorSelector && <div className="mt-4">{instructorSelector}</div>}
    {!introduction && <div className="my-5 text-center">
      <p className="text-sm text-muted-foreground">{preparation.remaining}</p>
      <p role="timer" aria-label={preparation.remaining} className="mt-2 font-display text-5xl tabular-nums">
        {Math.floor(value.remainingSeconds / 60)}:{String(value.remainingSeconds % 60).padStart(2, '0')}
      </p>
      {value.remainingSeconds === 0 && <p role="status" className="mt-3 text-sm">{preparation.ready}</p>}
    </div>}
    <button type="button" disabled={paused || hidden || speech.blocked}
      onClick={() => void speech.listen()}
      className="mt-3 flex min-h-11 items-center gap-2 rounded-xl px-3 text-sm font-medium text-primary disabled:opacity-40">
      {speech.busy ? <Square size={17} aria-hidden="true" /> : <Volume2 size={17} aria-hidden="true" />}
      {t(speech.busy ? 'fitness.coach.stop' : 'fitness.coach.listen')}
      {speech.loading && <Loader2 size={14} className="animate-spin" aria-label={t('fitness.coach.loading')} />}
    </button>
    {(speech.blocked || speech.message) && <p role="status" className="mt-1 text-xs text-muted-foreground">
      {t('fitness.coach.' + (speech.blocked ? 'audioUnavailable' : speech.message))}
    </p>}
    <div className="mt-5 grid gap-2">
      <button type="button" disabled={paused} onClick={() => action(introduction ? 'begin-warmup' : 'advance')}
        className="min-h-12 rounded-2xl bg-primary px-4 py-3 font-semibold text-primary-foreground disabled:opacity-40">
        {introduction ? preparation.begin : preparation.advance}
      </button>
      <button type="button" disabled={paused} onClick={() => action('skip')}
        className="min-h-11 rounded-2xl border border-border px-4 py-3 text-sm disabled:opacity-40">{preparation.skip}</button>
    </div>
  </section>;
}
